import React, { useState, useRef } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import { useAbsenceEntries } from 'contexts/absence-entries'
import { useOrganization } from 'contexts/organization'
import { size, pick } from 'utilities/object'
import { cls } from 'utilities/dom'
import { splitName } from 'utilities/file'
import { subYears, subMonths, subQuarters } from 'date-fns'
import { getYearRange, getQuarterRange, getMonthRange } from 'pages/absence/utilities'
import { statusTexts } from 'pages/absence/components/approval-status'
import Modal, { useModal } from 'modals/dialog'
import ExportAbsenceSlowMessage from './slow-message'
import Form from 'components/form/controller'
import FieldToucher from 'components/form/toucher'
import EntitiesField from 'components/form/field/entities'
import TimeField from 'components/form/field/time'
import OneOfField from 'components/form/field/one-of'
import PeopleField from 'components/form/field/people'
import { FileNameIcon, FileName } from './s'
import Message from 'components/message'
import Actions from 'components/form/actions'
import { Plain, ButtonSubmit } from 'components/button'
import Paragraph from 'components/typography/paragraph'
import { VismaIcon } from 'pages/integrations/utilities/icon'

const ExportAbsenceDetails = ({ salt }) => {
    const { formatMessage } = useIntl()
    const { dismiss } = useModal()

    const header = useRef()

    const groupsRef = useRef()
    const peopleRef = useRef()

    const { exportEntries } = useAbsenceEntries()
    const { organization } = useOrganization()

    const statusOptions = useStatuses()

    const [selectionType, setSelectionType] = useState('all')
    const [groups, setGroups] = useState([])
    const [people, setPeople] = useState([])
    const [statuses, setStatuses] = useState([])
    const [exportFormat, setExportFormat] = useState(exportFormats.xlsx)

    const [exporting, setExporting] = useState(false)
    const [error, setError] = useState(false)
    const [started, setStarted] = useState(false)

    const generate = async ({ types, time, selectionType, groups, people, exportFormat, ...body }) => {
        setExporting(true)

        types = (types ?? []).map(({ id }) => id)
        const [fromDate, toDate] = time ?? []

        body = {
            ...body,
            type: 'details',
            ...(types.length ? { types } : null),
            ...(fromDate ? { fromDate } : null),
            ...(toDate ? { toDate } : null)
        }

        if(selectionType === 'groups' && !!groups?.length) {
            body.groups = groups.map(group => pick(group, 'type', 'id'))
        }

        if(selectionType === 'people' && people?.length) {
            body.users = people.map(({ id }) => id)
        }

        body.format = exportFormats[exportFormat].format

        if(['PAX', 'VISMA_LON_600'].includes(body.format)) {
            body.statuses = ['approved']
        } else {
            if(statuses.length) {
                body.statuses = statuses.map(({ id }) => id)
            } else {
                body.statuses = statusOptions.map(({ id }) => id)
            }
        }

        const { ok } = await exportEntries(body)

        setExporting(false)

        if(ok) {
            setStarted(true)
            header.current?.scrollIntoView({ behavior: 'smooth' })
        } else {
            setError(true)
        }
    }

    const reset = () => {
        setError(false)
        setExporting(false)
        setStarted(false)
    }

    return (
        <>
            <Modal.Header
                heading={formatMessage({
                    id: 'absence_action_export_details',
                    defaultMessage: 'Export absence details'
                })}
                caption={formatMessage({
                    id: 'absence_export_type_details_helper',
                    defaultMessage: 'Detailed report of registered absence'
                })}
                ref={header} />
            <Form
                layout="vertical"
                onChange={reset}
                onSubmit={generate}>
                {({ errors, trigger }) => (
                    <>
                        <Modal.Body>
                            {started && <ExportAbsenceSlowMessage />}
                            <EntitiesField
                                name="types"
                                label={formatMessage({
                                    id: 'noun_absence_types',
                                    defaultMessage: 'Absence types'
                                })}
                                field={{
                                    placeholder: formatMessage({
                                        id: 'absence_export_absence_types_placeholder',
                                        defaultMessage: 'Pick absence types or leave empty for all'
                                    })
                                }}
                                controlProps={{ autoFocus: true }}
                                entity={{ type: 'absenceType' }}
                                picker={{
                                    heading: formatMessage({
                                        id: 'absence_types_action_pick',
                                        defaultMessage: 'Pick absence types'
                                    }),
                                    query: {
                                        key: ['absence', 'types'],
                                        path: '/absence/types',
                                        adaptItems: types => types
                                            .sort((one, two) => Number(two?.enabled ?? true) - Number(one?.enabled ?? true))
                                            .map(type => ({
                                                ...type,
                                                ...(!type?.enabled ? {
                                                    softDisabled: true,
                                                    tooltip: formatMessage({
                                                        id: 'absence_type_picker_disabled_tooltip',
                                                        defaultMessage: 'Disabled absence type. While new entries of this type can’t be created, you can choose to include already existing ones in the report.'
                                                    })
                                                } : null)
                                            }))
                                    }
                                }} />
                            <TimeField
                                salt={salt}
                                label={formatMessage({
                                    id: 'absence_type_label_time_period',
                                    defaultMessage: 'Time period'
                                })}
                                name="time"
                                field={{
                                    value: getMonthRange({ date: subMonths(new Date(), 1) }),
                                    range: true,
                                    required: true,
                                    include: 'always',
                                    unsettable: false
                                }}
                                picker={{
                                    shortcuts: [
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_year_previous',
                                                defaultMessage: 'Last year'
                                            }),
                                            range: getYearRange({ date: subYears(new Date(), 1) })
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_year_current',
                                                defaultMessage: 'This year'
                                            }),
                                            range: getYearRange()
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_quarter_previous',
                                                defaultMessage: 'Last quarter'
                                            }),
                                            range: getQuarterRange({ date: subQuarters(new Date(), 1) })
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_quarter_current',
                                                defaultMessage: 'This quarter'
                                            }),
                                            range: getQuarterRange()
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_month_previous',
                                                defaultMessage: 'Last month'
                                            }),
                                            range: getMonthRange({ date: subMonths(new Date(), 1) })
                                        },
                                        {
                                            label: formatMessage({
                                                id: 'date_range_predefined_month_current',
                                                defaultMessage: 'This month'
                                            }),
                                            range: getMonthRange()
                                        }
                                    ]
                                }} />
                            <FieldToucher name="time" />
                            <OneOfField
                                salt={salt}
                                label={formatMessage({
                                    id: 'absence_export_include_who_label',
                                    defaultMessage: 'Who should the report include?'
                                })}
                                name="selectionType"
                                field={{
                                    value: 'all',
                                    include: 'always',
                                    options: [
                                        {
                                            value: 'all',
                                            label: formatMessage({
                                                id: 'organization_everyone_in',
                                                defaultMessage: 'Everyone in {name}'
                                            }, organization),
                                            content: null
                                        },
                                        {
                                            value: 'groups',
                                            label: formatMessage({
                                                id: 'groups_members_of_generic',
                                                defaultMessage: 'Members of groups'
                                            }),
                                            controlRef: groupsRef,
                                            content: (
                                                <EntitiesField
                                                    name="groups"
                                                    label={false}
                                                    field={{ value: groups }}
                                                    picker={{
                                                        heading: formatMessage({
                                                            id: 'groups_action_pick',
                                                            defaultMessage: 'Pick groups'
                                                        }),
                                                        query: {
                                                            key: ['groups'],
                                                            path: '/groups',
                                                            filterOptions: {
                                                                overrides: {
                                                                    search: {
                                                                        controlProps: {
                                                                            placeholder: formatMessage({
                                                                                id: 'groups_action_find',
                                                                                defaultMessage: 'Find groups'
                                                                            })
                                                                        }
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                    onChange={({ groups }) => setGroups(groups)}
                                                    ref={groupsRef} />
                                            )
                                        },
                                        {
                                            value: 'people',
                                            label: formatMessage({
                                                id: 'people_individuals',
                                                defaultMessage: 'Individual people'
                                            }),
                                            controlRef: peopleRef,
                                            content: (
                                                <PeopleField
                                                    name="people"
                                                    label={false}
                                                    field={{ value: people }}
                                                    onChange={({ people }) => setPeople(people)}
                                                    ref={peopleRef} />
                                            )
                                        }
                                    ]
                                }}
                                onChange={({ selectionType }) => setSelectionType(selectionType)} />
                            {(!['pax', 'tlu'].includes(exportFormat.extension)) && (
                                <EntitiesField
                                    name="statuses"
                                    label={formatMessage({
                                        id: 'statuses_current',
                                        defaultMessage: 'Current statuses'
                                    })}
                                    field={{
                                        value: statuses,
                                        placeholder: formatMessage({
                                            id: 'export_status_pick_placeholder',
                                            defaultMessage: 'Pick statuses or leave empty for all'
                                        })
                                    }}
                                    entity={{ type: 'absenceStatus' }}
                                    picker={{ fixed: statusOptions }}
                                    onChange={({ statuses }) => setStatuses(statuses)} />
                            )}
                            {['pax', 'tlu'].includes(exportFormat.extension) && (
                                <>
                                    <EntitiesField
                                        name="statuses"
                                        label={formatMessage({
                                            id: 'statuses_current',
                                            defaultMessage: 'Current statuses'
                                        })}
                                        field={{ value: [statusOptions.find(({ id }) => id === 'approved')] }}
                                        entity={{ type: 'absenceStatus' }}
                                        enabled={false} />
                                    {(exportFormat.extension === 'pax') && (
                                        <Message
                                            type="info"
                                            message={formatMessage({
                                                id: 'absence_export_paxml_status_support_info',
                                                defaultMessage: 'PAXml only supports exporting approved absences.'
                                            })} />
                                    )}
                                    {(exportFormat.extension === 'tlu') && (
                                        <Message
                                            type="info"
                                            message={formatMessage({
                                                id: 'absence_export_visma_lon_600_status_support_info',
                                                defaultMessage: 'Visma Lön 600 only supports exporting approved absences.'
                                            })} />
                                    )}
                                </>
                            )}
                            <OneOfField
                                salt={salt}
                                label={formatMessage({
                                    id: 'document_label_format',
                                    defaultMessage: 'Format'
                                })}
                                name="exportFormat"
                                field={{
                                    value: exportFormat.extension,
                                    include: 'always',
                                    required: true,
                                    options: Object.values(exportFormats).map(({ extension, fileName, displayName, content: Content = null }) => ({
                                        value: extension,
                                        label: (
                                            <>
                                                {(extension !== 'tlu') && (
                                                    <FileNameIcon
                                                        {...splitName(fileName)}
                                                        displayName={formatMessage(displayName)}
                                                        inline={true} />
                                                )}
                                                {(extension === 'tlu') && (
                                                    <FileName>
                                                        <VismaIcon size={24} />
                                                        <span>
                                                            <FormattedMessage {...displayName} />
                                                        </span>
                                                    </FileName>
                                                )}
                                            </>
                                        ),
                                        ...(Content ? { content: <Content /> } : null)
                                    }))
                                }}
                                onChange={({ exportFormat }) => setExportFormat(exportFormats[exportFormat])} />
                            {!!error && (
                                <Message
                                    type="error"
                                    message={formatMessage({
                                        id: 'download_failure',
                                        defaultMessage: 'There was an error downloading the file.\nPlease try again in a short while.'
                                    })} />

                            )}
                            {!error && (
                                <Message
                                    type="info"
                                    message={formatMessage({
                                        id: (exportFormat.extension !== 'tlu') ?
                                            'absence_export_details_time_period_information'
                                            : 'absence_export_summary_time_period_information',
                                        defaultMessage: (exportFormat.extension !== 'tlu') ?
                                            'Absence entries, even the ones that only partially occur within the specified time period, will be included in their entirety in the report.'
                                            : 'Only the absence days occurring within the specified time period will be included in the report.'
                                    })} />
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Actions>
                                <Plain
                                    onClick={dismiss}
                                    disabled={exporting}>
                                    <FormattedMessage
                                        id="action_cancel"
                                        defaultMessage="Cancel" />
                                </Plain>
                                <ButtonSubmit
                                    className={cls('constructive', exporting && 'loading')}
                                    disabled={
                                        !!size(errors) ||
                                        (selectionType === 'groups' && !groups.length) ||
                                        (selectionType === 'people' && !people.length)
                                    }
                                    ref={trigger}>
                                    <FormattedMessage
                                        id="action_generate_report"
                                        defaultMessage="Generate export" />
                                </ButtonSubmit>
                            </Actions>
                        </Modal.Footer>
                    </>
                )}
            </Form>
        </>
    )
}

const useStatuses = () => {
    const { formatMessage } = useIntl()

    return Object.entries(statusTexts).map(([status, translation]) => ({
        id: status,
        name: formatMessage(translation)
    }))
}

const exportFormats = {
    xlsx: {
        extension: 'xlsx',
        format: 'XLSX',
        fileName: 'Huma Absence Export.xlsx',
        displayName: {
            id: 'file_format_xlsx',
            defaultMessage: 'XLSX (Excel spreadsheet)'
        }
    },
    pax: {
        extension: 'pax',
        format: 'PAX',
        fileName: 'Huma Absence Export.pax',
        displayName: {
            id: 'file_format_pax',
            defaultMessage: 'XML (PAXml)'
        },
        content: () => (
            <Paragraph className="caption compact">
                <FormattedMessage
                    id="export_pax_disclaimer"
                    defaultMessage="Users who have neither an employment ID nor a national identification number set, will be excluded from the export." />
            </Paragraph>
        )
    },
    // tlu: {
    //     extension: 'tlu',
    //     format: 'VISMA_LON_600',
    //     fileName: 'Huma Absence Export.tlu',
    //     displayName: {
    //         id: 'file_format_visma_lon_600',
    //         defaultMessage: 'Visma Lön 600 (XML)'
    //     }
    // }
}

export default ExportAbsenceDetails